import Autoplay from 'embla-carousel-autoplay'
import Link from 'next/link'
import * as React from 'react'

import { sectionSchemaRegistry } from '@/lib/section-schema-registry'
import useLocale from '@/hooks/use-locale'
import useMediaQuery from '@/hooks/use-media-query'

import type { FeedbackType } from '@/data/feedback'
import { feedbacks as defaultFeedbacksVi } from '@/data/feedback'
import { feedbacksEng as defaultFeedbacksEn } from '@/data/feedbacks-en'

import SectionHeader from '@/components/common/section-header'

import { FEEDBACK_URL } from '@/constant/app'

import FeedbackCard from './feedback-card'
import { Button } from '../ui/button'
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../ui/carousel'

import type { SectionElementSchema } from '@/types/element-schema.type'

interface FeedbackItem {
  id: string | number
  name?: string
  role?: string
  content: string
  avatar?: string
  rating?: number
  experience?: string
  company?: string
  course?: string
}

interface FeedbackSectionProps {
  className?: string
  /** Section heading */
  heading?: string
  /** Section subtitle */
  subtitle?: string
  /** Heading text color */
  headingColor?: string
  /** Subtitle text color */
  subtitleColor?: string
  /** Section background color */
  bgColor?: string
  /** Number of cards per row */
  cardColumns?: 2 | 3 | 4
  /** Card visual style */
  cardStyle?: 'default' | 'bordered' | 'shadow'
  /** Data source: 'built-in' or 'custom' */
  dataSource?: 'built-in' | 'custom'
  /** Number of items to show (built-in mode) */
  pageSize?: number
  /** Custom feedbacks (custom mode) */
  feedbacks?: FeedbackItem[]
  /** Enable autoplay */
  autoplay?: boolean
  /** Autoplay delay in ms */
  autoplayDelay?: number
  /** Enable infinite loop */
  loop?: boolean
  /** Show navigation arrows */
  showNavigation?: boolean
  /** Show "View All" button */
  showViewAllButton?: boolean
  /** View all button URL */
  viewAllUrl?: string
  /** View all button text */
  viewAllText?: string
}

const defaultCustomFeedbacks: FeedbackItem[] = [
  {
    id: 1,
    name: 'Nguyen Van A',
    role: 'AI Engineer',
    content: 'Khóa học rất chất lượng, kiến thức thực tế và mentor nhiệt tình!',
    avatar: '/images/nguyen_tran_dang_duong.jpeg',
    rating: 5,
    experience: '2+ năm',
    company: 'FPT Smart Cloud',
    course: 'Course 1',
  },
  {
    id: 2,
    name: 'Tran Thi B',
    role: 'ML Engineer',
    content: 'Nội dung bài bản, từ cơ bản đến nâng cao. Rất đáng để đầu tư!',
    avatar: '/images/dung_hoang.jpeg',
    rating: 5,
    experience: '4+ năm',
    company: 'Viettel',
    course: 'Course 2',
  },
  {
    id: 3,
    name: 'Le Van C',
    role: 'Data Scientist',
    content: 'Giảng viên tâm huyết, luôn sẵn sàng hỗ trợ học viên mọi lúc.',
    avatar: '/images/tien_nguyen.jpeg',
    rating: 5,
    experience: '2+ năm',
    company: 'Momo',
    course: 'Course 1',
  },
]

// Element schema for Puck editor
export const feedbackSectionSchema: SectionElementSchema = {
  componentType: 'FeedbackSection',
  label: 'Feedback Section',
  category: 'content',
  description: 'Testimonials and feedback carousel with full customization',
  elements: [
    // ── Section Header ──
    {
      key: 'heading',
      type: 'textarea',
      label: 'Section Heading',
      helpText: 'Main title displayed above the feedback carousel',
    },
    {
      key: 'subtitle',
      type: 'textarea',
      label: 'Subtitle',
      helpText: 'Secondary text below the heading',
    },
    // ── Layout & Styling ──
    {
      key: 'headingColor',
      type: 'color',
      label: '🎨 Heading Color',
      helpText: 'Leave empty for default theme color',
      default: '',
    },
    {
      key: 'subtitleColor',
      type: 'color',
      label: '🎨 Subtitle Color',
      helpText: 'Leave empty for default theme color',
      default: '',
    },
    {
      key: 'bgColor',
      type: 'color',
      label: '🎨 Background Color',
      helpText: 'Section background color (transparent = no background)',
      default: 'transparent',
    },
    {
      key: 'cardColumns',
      type: 'select',
      label: '📐 Cards Per Row',
      helpText: 'Number of feedback cards displayed per row on desktop',
      options: [
        { label: '2 Cards per row', value: 2 },
        { label: '3 Cards per row (Default)', value: 3 },
        { label: '4 Cards per row', value: 4 },
      ],
      default: 3,
    },
    {
      key: 'cardStyle',
      type: 'select',
      label: 'Card Style',
      helpText: 'Visual style for feedback cards',
      options: [
        { label: 'Default (Glass effect)', value: 'default' },
        { label: 'Bordered (Subtle border)', value: 'bordered' },
        { label: 'Shadow (Elevated cards)', value: 'shadow' },
      ],
      default: 'default',
    },
    // ── Data Source & Items ──
    {
      key: 'dataSource',
      type: 'select',
      label: '📦 Data Source',
      helpText: 'Choose built-in testimonials or create custom items',
      options: [
        { label: 'Built-in (Default testimonials)', value: 'built-in' },
        { label: 'Custom Items (Add your own)', value: 'custom' },
      ],
      default: 'built-in',
    },
    {
      key: 'pageSize',
      type: 'select',
      label: '🔢 Number of Items (Built-in mode)',
      helpText: 'Maximum items to display when using built-in data',
      options: [
        { label: '3 Items', value: 3 },
        { label: '5 Items (All)', value: 5 },
        { label: '6 Items', value: 6 },
        { label: '9 Items', value: 9 },
      ],
      default: 5,
    },
    {
      key: 'feedbacks',
      type: 'array',
      label: '✏️ Custom Feedback Items',
      helpText: 'Add, edit, or remove feedback cards (only used when Data Source is "Custom Items")',
      default: defaultCustomFeedbacks,
      children: [
        {
          key: 'name',
          type: 'text',
          label: '👤 Full Name',
          helpText: 'Name of the person giving feedback',
          default: 'New Reviewer',
        },
        {
          key: 'role',
          type: 'text',
          label: '💻 Position / Vị trí',
          helpText: 'e.g. "AI Engineer" or "ML Engineer"',
          default: 'AI Engineer',
        },
        {
          key: 'company',
          type: 'text',
          label: '🏢 Company / Công ty',
          helpText: 'e.g. "FPT Smart Cloud" or "Viettel"',
          default: 'Tech Company',
        },
        {
          key: 'experience',
          type: 'text',
          label: '🤖 Experience / Kinh nghiệm',
          helpText: 'e.g. "2+ năm" or "5+ years"',
          default: '2+ năm',
        },
        {
          key: 'course',
          type: 'text',
          label: '🎓 Student / Học viên',
          helpText: 'e.g. "Course 1" or "Course 2"',
          default: 'Course 1',
        },
        {
          key: 'content',
          type: 'textarea',
          label: '💬 Feedback Content',
          helpText: 'The testimonial text (required)',
          required: true,
          default: 'Khóa học rất tuyệt vời!',
        },
        {
          key: 'avatar',
          type: 'image',
          label: '🖼️ Avatar Image',
          helpText: 'Profile photo URL (leave empty for initials)',
          default: '/images/nguyen_tran_dang_duong.jpeg',
        },
      ],
    },
    // ── Carousel Controls ──
    {
      key: 'autoplay',
      type: 'boolean',
      label: '▶️ Enable Autoplay',
      helpText: 'Automatically slide through feedback cards',
      default: true,
    },
    {
      key: 'autoplayDelay',
      type: 'select',
      label: '⏱️ Autoplay Speed',
      helpText: 'Time between each auto-slide',
      options: [
        { label: 'Fast (2 seconds)', value: 2000 },
        { label: 'Normal (2.5 seconds)', value: 2500 },
        { label: 'Slow (3 seconds)', value: 3000 },
        { label: 'Very Slow (5 seconds)', value: 5000 },
      ],
      default: 2500,
    },
    {
      key: 'loop',
      type: 'boolean',
      label: '🔁 Infinite Loop',
      helpText: 'Continuously loop through items without stopping at the end',
      default: true,
    },
    {
      key: 'showNavigation',
      type: 'boolean',
      label: '⬅️➡️ Show Navigation Arrows',
      helpText: 'Display previous/next buttons on the carousel',
      default: true,
    },
    // ── View All Button ──
    {
      key: 'showViewAllButton',
      type: 'boolean',
      label: '🔗 Show "View All" Button',
      helpText: 'Display a button linking to all feedbacks',
      default: true,
    },
    {
      key: 'viewAllUrl',
      type: 'text',
      label: '🔗 View All URL',
      helpText: 'Link destination for the "View All" button',
      default: FEEDBACK_URL,
    },
    {
      key: 'viewAllText',
      type: 'text',
      label: '🔗 View All Button Text',
      helpText: 'Custom label for the button (leave empty for default)',
    },
    // ── Advanced ──
    {
      key: 'className',
      type: 'text',
      label: '⚙️ CSS Class',
      helpText: 'Additional CSS classes for advanced styling',
    },
  ],
}

// Register schema
sectionSchemaRegistry.register(feedbackSectionSchema)

// Card column CSS class map (keys are strings because Puck select returns string values)
const columnClassMap: Record<string, string> = {
  '2': 'sm:basis-1/2 lg:basis-1/2',
  '3': 'sm:basis-1/2 lg:basis-1/3',
  '4': 'sm:basis-1/2 lg:basis-1/4',
}

// Card style CSS class map
const cardStyleClassMap: Record<string, string> = {
  default: 'bg-white/10 backdrop-blur-md',
  bordered: 'bg-white border-2 border-primary/20',
  shadow: 'bg-white shadow-xl hover:shadow-2xl transition-shadow duration-300',
}

const FeedbackSection = React.forwardRef<HTMLDivElement, FeedbackSectionProps>(
  (
    {
      className,
      heading,
      subtitle,
      headingColor,
      subtitleColor,
      bgColor = 'transparent',
      cardColumns = 3,
      cardStyle = 'default',
      dataSource = 'built-in',
      pageSize = 5,
      feedbacks: customFeedbacks,
      autoplay = true,
      autoplayDelay = 2500,
      loop = true,
      showNavigation = true,
      showViewAllButton = true,
      viewAllUrl = FEEDBACK_URL,
      viewAllText,
    },
    ref
  ) => {
    const { text, lang } = useLocale()
    const isDesktop = useMediaQuery('(min-width: 1024px)')

    const autoplayEnabled = autoplay === true || (autoplay as unknown) === 'true'
    const loopEnabled = loop === true || (loop as unknown) === 'true'

    // FIXME: autoplayPlugin is not working as expected
    const autoplayPlugin = React.useMemo(
      () => (autoplayEnabled ? Autoplay({ delay: Number(autoplayDelay) || 2500, stopOnInteraction: false }) : null),
      [autoplayEnabled, autoplayDelay]
    )

    // Determine which data to use
    const defaultFeedbacks = lang === 'vi' ? defaultFeedbacksVi : defaultFeedbacksEn
    const displayFeedbacks =
      dataSource === 'custom' && customFeedbacks?.length ? customFeedbacks : defaultFeedbacks.slice(0, pageSize)

    // Convert FeedbackItem to FeedbackType if needed
    const normalizedFeedbacks = displayFeedbacks.map((f, index) => {
      if ('content' in f && !('message' in f)) {
        return {
          id: f.id || index,
          message: f.content,
          author: {
            username: f.name || 'Anonymous',
            avatar: f.avatar || '',
            company: f.company || '',
            position: f.role || '',
            experience: f.experience || '',
            course: f.course || '',
            createAt: new Date(),
          },
          repository: {
            href: '',
            start: 0,
            fork: 0,
            name: '',
            image: '',
          },
        }
      }
      return f
    }) as FeedbackType[]

    const displayHeading = heading || text.MAIN.BODY.HEADING_6
    const displayViewAllText = viewAllText || text.MAIN.BODY.FEEDBACK_BUTTON.EXPAND
    const columnClass = columnClassMap[String(cardColumns)] || columnClassMap['3']
    const styleClass = cardStyleClassMap[cardStyle] || ''
    const hasBg = bgColor && bgColor !== 'transparent'

    return (
      <section
        className={`container lg:px-16 ${className || ''}`}
        id="reviews"
        ref={ref}
        style={{ backgroundColor: hasBg ? bgColor : undefined }}
      >
        <SectionHeader
          title={displayHeading}
          subtitle={subtitle || ''}
          className="items-center text-center"
          titleStyle={headingColor ? { color: headingColor } : undefined}
          subtitleStyle={subtitleColor ? { color: subtitleColor } : undefined}
        />
        <div className="mt-8 md:mt-12 lg:mt-16">
          <Carousel
            key={`carousel-${autoplayEnabled}-${loopEnabled}`}
            className="w-full relative"
            plugins={autoplayPlugin ? [autoplayPlugin] : []}
            onMouseEnter={autoplayPlugin ? autoplayPlugin.stop : undefined}
            onMouseLeave={autoplayPlugin ? autoplayPlugin.reset : undefined}
            opts={{
              loop: loopEnabled,
            }}
          >
            <CarouselContent className="-ml-6 h-full">
              {normalizedFeedbacks.map(feedback => (
                <CarouselItem key={feedback.id} className={`pl-6 md:pl-6 ${columnClass}`}>
                  <FeedbackCard feedback={feedback} className={`h-full ${styleClass}`} />
                </CarouselItem>
              ))}
            </CarouselContent>
            {showNavigation &&
              (isDesktop ? (
                <>
                  <CarouselPrevious />
                  <CarouselNext />
                </>
              ) : (
                <div className="flex justify-center mt-8 gap-4">
                  <CarouselPrevious className="static translate-y-0 mx-0" />
                  <CarouselNext className="static translate-y-0 mx-0" />
                </div>
              ))}
          </Carousel>
          {showViewAllButton && (
            <div className="flex justify-center mt-8 lg:mt-12">
              <Button variant="outline" asChild>
                <Link href={viewAllUrl} target="_blank" rel="noopener noreferrer">
                  {displayViewAllText}
                </Link>
              </Button>
            </div>
          )}
        </div>
      </section>
    )
  }
)

FeedbackSection.displayName = 'FeedbackSection'

export default FeedbackSection
